import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen, 
  CheckCircle,
  XCircle,
  ChevronDown,
  User,
  Plus,
  Bell,
  AlertCircle,
  X
} from 'lucide-react';
import moment from 'moment';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAppointments: 0,
    upcomingAppointments: 0,
    completedAppointments: 0,
    cancelledAppointments: 0,
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const hasAutoSelectedStudent = useRef(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch appointments for the user or selected student
      let appointmentsResponse;
      if (user?.role === 'parent' && selectedStudent) {
        // Get appointments for specific student
        appointmentsResponse = await api.get(`/students/${selectedStudent._id}/appointments`);
        const appointments = appointmentsResponse.data.appointments;
        setRecentAppointments(appointments.slice(0, 5));
      } else {
        // Get appointments for the user
        appointmentsResponse = await api.get('/appointments', {
          params: { limit: 10 }
        });
        const appointments = appointmentsResponse.data.appointments;
        setRecentAppointments(appointments.slice(0, 5));
      }
      
      // Calculate statistics using the fetched appointments
      const appointments = appointmentsResponse.data.appointments || [];
      const now = new Date();
      const total = appointments.length;
      const upcoming = appointments.filter(apt => 
        new Date(apt.scheduledDate) > now && 
        !['cancelled', 'completed'].includes(apt.status)
      ).length;
      const completed = appointments.filter(apt => 
        apt.status === 'completed'
      ).length;
      const cancelled = appointments.filter(apt => 
        apt.status === 'cancelled'
      ).length;

      setStats({
        totalAppointments: total,
        upcomingAppointments: upcoming,
        completedAppointments: completed,
        cancelledAppointments: cancelled,
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role, selectedStudent?._id]);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await api.get('/students');
      const fetchedStudents = response.data.students;
      setStudents(fetchedStudents);
      
      // Auto-select first student if available and we haven't auto-selected yet
      if (fetchedStudents.length > 0 && !selectedStudent && !hasAutoSelectedStudent.current) {
        setSelectedStudent(fetchedStudents[0]);
        hasAutoSelectedStudent.current = true;
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, [selectedStudent]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/users/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadNotificationsCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    if (user?.role === 'parent' || user?.role === 'admin') {
      fetchNotifications();
    }
  }, [fetchDashboardData, fetchNotifications, user?.role]);

  useEffect(() => {
    if (user?.role === 'parent') {
      fetchStudents();
    }
  }, [user?.role, fetchStudents]);

  // Notification Handlers
  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await api.put(`/users/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n._id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await api.delete(`/users/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      const deletedNotification = notifications.find(n => n._id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };


  // Quick Action Handlers
  const handleQuickAction = (action) => {
    switch (action) {
      case 'manageUsers':
        navigate('/users');
        break;
      case 'createAppointment':
        navigate('/appointments');
        break;
      case 'viewSchedule':
        navigate('/schedules');
        break;
      case 'updateNotes':
        navigate('/appointments');
        break;
      case 'requestReschedule':
        navigate('/appointments');
        break;
      case 'manageStudents':
        navigate('/students');
        break;
      default:
        break;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { class: 'status-available', text: 'Available' },
      booked: { class: 'status-booked', text: 'Booked' },
      scheduled: { class: 'status-scheduled', text: 'Scheduled' },
      confirmed: { class: 'status-confirmed', text: 'Confirmed' },
      'in-progress': { class: 'status-in-progress', text: 'In Progress' },
      completed: { class: 'status-completed', text: 'Completed' },
      cancelled: { class: 'status-cancelled', text: 'Cancelled' },
      rescheduled: { class: 'status-rescheduled', text: 'Rescheduled' },
    };

    const config = statusConfig[status] || statusConfig.available;
    return <span className={config.class}>{config.text}</span>;
  };

  const getRoleSpecificContent = () => {
    switch (user?.role) {
      case 'admin':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">System Overview</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {stats.totalAppointments}
                    </div>
                    <div className="text-sm text-gray-500">Total Appointments</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success-600">
                      {stats.upcomingAppointments}
                    </div>
                    <div className="text-sm text-gray-500">Upcoming</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <button 
                    className="btn btn-primary w-full"
                    onClick={() => handleQuickAction('manageUsers')}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Manage Users
                  </button>
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => navigate('/schedules')}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Create Schedule
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
        );

      case 'teacher':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Today's Schedule</h3>
              </div>
              <div className="card-body">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">
                    {recentAppointments.filter(apt => 
                      moment(apt.scheduledDate).isSame(moment(), 'day')
                    ).length}
                  </div>
                  <div className="text-sm text-gray-500">Appointments Today</div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <button 
                    className="btn btn-primary w-full"
                    onClick={() => handleQuickAction('viewSchedule')}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    View Schedule
                  </button>
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => handleQuickAction('updateNotes')}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Update Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'parent':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Student Selector */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Current Student</h3>
              </div>
              <div className="card-body">
                {students.length === 0 ? (
                  <div className="text-center py-4">
                    <User className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-3">No students added yet</p>
                    <p className="text-xs text-gray-400">Contact an administrator to add students to your account</p>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowStudentSelector(!showStudentSelector)}
                      className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-primary-600 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {selectedStudent ? `${selectedStudent.firstName} ${selectedStudent.lastName}` : 'Select Student'}
                        </span>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showStudentSelector ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showStudentSelector && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {students.map((student) => (
                          <button
                            key={student._id}
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowStudentSelector(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                              selectedStudent?._id === student._id ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                            }`}
                          >
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2" />
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="text-xs text-gray-500 ml-6">
                              Grade {student.grade} • Age {student.currentAge}
                            </div>
                          </button>
                        ))}
                        <div className="border-t border-gray-200">
                          <button
                            onClick={() => {
                              setShowStudentSelector(false);
                              handleQuickAction('manageStudents');
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                          >
                            <Plus className="h-4 w-4 mr-2 inline" />
                            Manage Students
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => navigate('/calendar')}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    View Calendar
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Notifications display component
  const NotificationsSection = () => {
    // Show notifications for parents and admins
    if (user?.role !== 'parent' && user?.role !== 'admin') return null;
    
    const unreadNotifications = notifications.filter(n => !n.read);
    const readNotifications = notifications.filter(n => n.read);
    
    if (notifications.length === 0) {
      return null;
    }

    return (
      <div className="mb-8">
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="h-5 w-5 text-primary-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Notifications
                {unreadNotificationsCount > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {unreadNotificationsCount} new
                  </span>
                )}
              </h3>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {/* Unread Notifications */}
              {unreadNotifications.map((notification) => (
                <div
                  key={notification._id}
                  className="flex items-start justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <div className="flex items-start flex-1">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {moment(notification.createdAt).format('MMM D, YYYY [at] h:mm A')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => handleMarkNotificationRead(notification._id)}
                      className="text-xs text-gray-600 hover:text-gray-900"
                      title="Mark as read"
                    >
                      Mark read
                    </button>
                    <button
                      onClick={() => handleDeleteNotification(notification._id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Read Notifications */}
              {readNotifications.map((notification) => (
                <div
                  key={notification._id}
                  className="flex items-start justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg opacity-75"
                >
                  <div className="flex items-start flex-1">
                    <Bell className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {moment(notification.createdAt).format('MMM D, YYYY [at] h:mm A')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteNotification(notification._id)}
                    className="text-gray-400 hover:text-red-600 ml-2"
                    title="Delete"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back, {user?.firstName || user?.email}!</p>
      </div>

      {/* Role-specific content */}
      {getRoleSpecificContent()}
      
      {/* Notifications Section (for parents) */}
      <NotificationsSection />
      
      {/* Recent Appointments */}
      <div className="card mb-8">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Recent Appointments</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading...</p>
            </div>
          ) : recentAppointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-sm text-gray-500">No recent appointments</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student/Teacher
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentAppointments.map((appointment) => (
                    <tr key={appointment._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{moment(appointment.scheduledDate).format('MMM D, YYYY')}</div>
                        <div className="text-gray-500">
                          {appointment.startTime} - {appointment.endTime}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user?.role === 'parent' ? (
                          appointment.teacher ? (
                            <>
                              {appointment.teacher.firstName} {appointment.teacher.lastName}
                            </>
                          ) : (
                            'N/A'
                          )
                        ) : (
                          appointment.student ? (
                            <>
                              {appointment.student.firstName} {appointment.student.lastName}
                            </>
                          ) : (
                            'N/A'
                          )
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {appointment.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(appointment.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;